import { useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { CASE_STUDIES, type CaseStudy } from "@/lib/caseStudiesData";
import {
  ChevronDown,
  ChevronRight,
  BookOpen,
  Users,
  ClipboardList,
  AlertTriangle,
  Target,
  CheckSquare,
  Upload,
  Loader2,
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const DIFFICULTY_STYLES: Record<string, string> = {
  beginner: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  intermediate: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  advanced: "bg-rose-500/15 text-rose-700 border-rose-500/30",
};

const RISK_DOT: Record<string, string> = {
  low: "bg-emerald-500",
  medium: "bg-amber-500",
  high: "bg-rose-500",
};

const INFLUENCE_BADGE: Record<string, string> = {
  high: "bg-indigo-500/15 text-indigo-700 border-indigo-500/30",
  medium: "bg-sky-500/15 text-sky-700 border-sky-500/30",
  low: "bg-slate-500/15 text-slate-600 border-slate-400/30",
};

interface Props {
  baProjectId: string | null;
}

export default function CaseStudiesPanel({ baProjectId }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [expanded, setExpanded] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<string | null>(null);
  const [loadedIds, setLoadedIds] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpanded((prev) => (prev === id ? null : id));
    if (!activeSection[id]) {
      setActiveSection((prev) => ({ ...prev, [id]: "overview" }));
    }
  };

  const setSection = (caseId: string, section: string) => {
    setActiveSection((prev) => ({ ...prev, [caseId]: section }));
  };

  const handleLoad = async (cs: CaseStudy) => {
    if (!baProjectId) {
      alert("Visit the Requirements tab first to initialise the project backend.");
      return;
    }
    setLoading(cs.id);
    let successCount = 0;
    let failCount = 0;
    for (const req of cs.requirements) {
      try {
        await axios.post(
          `${BASE}/api/tools/requirements`,
          {
            project_id: baProjectId,
            title: req.title,
            raw_text: req.raw_text,
            req_type: req.req_type,
            feature: req.feature,
            channel: req.channel,
            contains_phi: req.contains_phi,
            market: req.market,
            sensitivity_flags: req.sensitivity_flags,
            priority: req.priority,
            domain: req.domain,
            assumptions: req.assumptions ? [req.assumptions] : [],
            constraints_list: req.constraints_list ? [req.constraints_list] : [],
          },
          { headers: authHeader }
        );
        successCount++;
      } catch {
        failCount++;
      }
    }
    setLoading(null);
    if (failCount === 0) {
      setLoadedIds((prev) => new Set(Array.from(prev).concat(cs.id)));
      alert(`Loaded ${successCount} requirements into your project.`);
    } else {
      alert(`Loaded ${successCount} requirements. ${failCount} failed — check console for details.`);
    }
  };

  const sections = [
    { id: "overview", label: "Overview" },
    { id: "stakeholders", label: "Stakeholders" },
    { id: "requirements", label: "Requirements" },
    { id: "risks", label: "Risks" },
    { id: "exercises", label: "Exercises" },
  ];

  return (
    <div className="p-4 max-w-3xl space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <BookOpen className="h-4 w-4" /> BA Case Studies
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Practice business analysis with realistic scenarios. Load requirements directly into your project.
        </p>
      </div>

      {/* Case Study List */}
      <div className="space-y-2">
        {CASE_STUDIES.map((cs) => {
          const isOpen = expanded === cs.id;
          const section = activeSection[cs.id] || "overview";
          const isLoading = loading === cs.id;
          const isLoaded = loadedIds.has(cs.id);

          return (
            <div
              key={cs.id}
              className="border border-border rounded-md bg-card overflow-hidden"
            >
              {/* Card Header */}
              <button
                className="w-full flex items-start gap-3 p-3 hover:bg-secondary/30 transition-colors text-left"
                onClick={() => toggle(cs.id)}
              >
                <span className="mt-0.5 shrink-0 text-muted-foreground">
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-card-foreground">
                      {cs.title}
                    </span>
                    <span
                      className={`text-[10px] font-mono uppercase border rounded px-1.5 py-0.5 ${
                        DIFFICULTY_STYLES[cs.difficulty]
                      }`}
                    >
                      {cs.difficulty}
                    </span>
                    {isLoaded && (
                      <span className="text-[10px] font-mono uppercase border rounded px-1.5 py-0.5 bg-emerald-500/15 text-emerald-700 border-emerald-500/30 flex items-center gap-0.5">
                        <CheckSquare className="h-2.5 w-2.5" /> loaded
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    <span className="text-[11px] text-muted-foreground">{cs.industry}</span>
                    <span className="text-[11px] text-muted-foreground">·</span>
                    <span className="text-[11px] text-muted-foreground">{cs.phase}</span>
                    <span className="text-[11px] text-muted-foreground">·</span>
                    <span className="text-[11px] text-muted-foreground">~{cs.estimatedHours}h</span>
                    <span className="text-[11px] text-muted-foreground">·</span>
                    <span className="text-[11px] text-muted-foreground">
                      {cs.requirements.length} reqs · {cs.risks.length} risks
                    </span>
                  </div>
                </div>
              </button>

              {/* Expanded Content */}
              {isOpen && (
                <div className="border-t border-border">
                  {/* Section Tabs */}
                  <div className="flex items-center gap-0 border-b border-border bg-secondary/20 overflow-x-auto">
                    {sections.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setSection(cs.id, s.id)}
                        className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider shrink-0 transition-colors border-b-2 ${
                          section === s.id
                            ? "border-accent text-accent font-semibold"
                            : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                    <div className="flex-1" />
                    <button
                      onClick={() => handleLoad(cs)}
                      disabled={isLoading}
                      className="shrink-0 flex items-center gap-1.5 mx-2 my-1 text-[11px] font-medium border border-border rounded px-2.5 py-1 hover:border-accent/50 hover:text-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Upload className="h-3 w-3" />
                      )}
                      Load into Project
                    </button>
                  </div>

                  <div className="p-4 space-y-3">
                    {/* OVERVIEW */}
                    {section === "overview" && (
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-muted-foreground leading-relaxed">{cs.description}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Business Problem</p>
                          <p className="text-xs text-card-foreground leading-relaxed">{cs.businessProblem}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
                            <Target className="h-3 w-3" /> Objectives
                          </p>
                          <ul className="space-y-1">
                            {cs.objectives.map((obj, i) => (
                              <li key={i} className="text-xs text-card-foreground flex gap-2">
                                <span className="text-muted-foreground shrink-0">{i + 1}.</span>
                                <span>{obj}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider mb-1.5">Success Metrics</p>
                          <ul className="space-y-1">
                            {cs.successMetrics.map((m, i) => (
                              <li key={i} className="text-xs text-card-foreground flex gap-2">
                                <span className="text-muted-foreground shrink-0">—</span>
                                <span>{m}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* STAKEHOLDERS */}
                    {section === "stakeholders" && (
                      <div className="space-y-2">
                        <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                          <Users className="h-3 w-3" /> {cs.stakeholders.length} Stakeholders
                        </p>
                        {cs.stakeholders.map((s, i) => (
                          <div key={i} className="border border-border rounded px-3 py-2 bg-secondary/10">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-xs font-semibold text-card-foreground">{s.name}</p>
                                <p className="text-[11px] text-muted-foreground">{s.role}</p>
                              </div>
                              <span
                                className={`text-[10px] font-mono uppercase border rounded px-1.5 py-0.5 shrink-0 ${
                                  INFLUENCE_BADGE[s.influence]
                                }`}
                              >
                                {s.influence}
                              </span>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-1">{s.interest}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* REQUIREMENTS */}
                    {section === "requirements" && (
                      <div className="space-y-2">
                        <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                          <ClipboardList className="h-3 w-3" /> {cs.requirements.length} Requirements
                        </p>
                        {cs.requirements.map((req, i) => (
                          <div key={i} className="border border-border rounded px-3 py-2 bg-secondary/10 space-y-1">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-xs font-semibold text-card-foreground">{req.title}</p>
                              <div className="flex items-center gap-1 shrink-0">
                                <span className="text-[10px] font-mono border border-border rounded px-1.5 py-0.5 text-muted-foreground uppercase">
                                  {req.req_type}
                                </span>
                                <span className="text-[10px] font-mono border border-border rounded px-1.5 py-0.5 text-muted-foreground uppercase">
                                  {req.priority}
                                </span>
                                {req.contains_phi && (
                                  <span className="text-[10px] font-mono border border-destructive/30 rounded px-1.5 py-0.5 text-destructive bg-destructive/10 uppercase">
                                    PHI
                                  </span>
                                )}
                              </div>
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-relaxed">{req.raw_text}</p>
                            {req.feature && (
                              <p className="text-[10px] text-muted-foreground">Feature: {req.feature}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* RISKS */}
                    {section === "risks" && (
                      <div className="space-y-2">
                        <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" /> {cs.risks.length} Risks
                        </p>
                        {cs.risks.map((risk) => (
                          <div key={risk.id} className="border border-border rounded px-3 py-2 bg-secondary/10 space-y-1.5">
                            <div className="flex items-start gap-2">
                              <div className="flex gap-1 shrink-0 mt-0.5">
                                <span className={`inline-block h-2 w-2 rounded-full mt-0.5 ${RISK_DOT[risk.likelihood]}`} title={`Likelihood: ${risk.likelihood}`} />
                                <span className={`inline-block h-2 w-2 rounded-full mt-0.5 ${RISK_DOT[risk.impact]}`} title={`Impact: ${risk.impact}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-card-foreground">{risk.description}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-[10px] font-mono border border-border rounded px-1.5 py-0.5 text-muted-foreground">{risk.category}</span>
                                  <span className="text-[10px] text-muted-foreground">Owner: {risk.owner}</span>
                                </div>
                              </div>
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-relaxed">
                              <span className="font-semibold">Mitigation:</span> {risk.mitigation}
                            </p>
                          </div>
                        ))}
                        <div className="flex items-center gap-3 text-[10px] text-muted-foreground pt-1">
                          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500 inline-block" /> Low</span>
                          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500 inline-block" /> Medium</span>
                          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-rose-500 inline-block" /> High</span>
                          <span className="text-muted-foreground/60">Left dot = likelihood · Right dot = impact</span>
                        </div>
                      </div>
                    )}

                    {/* EXERCISES */}
                    {section === "exercises" && (
                      <div className="space-y-2">
                        <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">Practice Exercises</p>
                        {cs.practiceExercises.map((ex, i) => (
                          <div key={i} className="border border-border rounded px-3 py-2 bg-secondary/10 flex gap-2.5">
                            <span className="text-[11px] font-mono text-muted-foreground shrink-0 mt-0.5">{i + 1}.</span>
                            <p className="text-xs text-card-foreground leading-relaxed">{ex}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
